import React, { useState, useEffect, useMemo, useCallback } from "react";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";
import { useTranslation } from "react-i18next";

const IndexDataTable = (props) => {
  const { t } = useTranslation(["marketdata"]);
  const [tableState, setTableState] = useState({
    indexBases: [],
    market: {},
    product: null,
    isLoading: true,
  });

  const {
    indexBases,
    market,
    product,
    pricePrecision,
    volumePrecision,
    currencySymbol,
  } = props;

  // Memoize default column definitions
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      type: "centerAligned",
    }),
    []
  );

  // Map marks data to view model
  const mapToViewModel = useCallback((indexBases, market) => {
    if (!indexBases || !market) return [];

    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
    };

    const dateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return indexBases.map((indexBase) => {
      return {
        indexBaseId: indexBase.indexBaseId,
        marketDate: new Date(
          indexBase.marketDate.substring(0, 10) + "T00:00:00"
        ).toLocaleDateString("en-US", dateFormatOptions),
        marketName: indexBase.marketName,
        //priceBasisName: indexBase.priceBasisName,
        termName: indexBase.termName,
        markPrice: indexBase.markPrice
          ? currencySymbol + Number(indexBase.markPrice).toFixed(pricePrecision)
          : "",
        bestBid: indexBase.bestBid
          ? currencySymbol + Number(indexBase.bestBid).toFixed(pricePrecision)
          : "",
        bestOffer: indexBase.bestOffer
          ? currencySymbol + Number(indexBase.bestOffer).toFixed(pricePrecision)
          : "",
        tradeCount: indexBase.tradeCount ?? "",
        tradeVolume: indexBase.tradeVolume
          ? Number(
              Number(indexBase.tradeVolume).toFixed(volumePrecision)
            ).toLocaleString("en-US")
          : "",
        tradeWeightedAverage: indexBase.tradeWeightedAverage
          ? currencySymbol +
            Number(indexBase.tradeWeightedAverage).toFixed(pricePrecision)
          : "",
        updatedDate: new Date(indexBase.updatedDate).toLocaleDateString(
          "en-US",
          dateTimeFormatOptions
        ),
        ...indexBase.qualityAttributeValues?.reduce((acc, attribute) => {
          acc[attribute.qualityAttributeId] = attribute.value;
          return acc;
        }, {}),
      };
    });
  }, [currencySymbol, pricePrecision, volumePrecision]);

  // Memoize column definitions
  const columnDefs = useMemo(() => {
    if (!tableState.product) return [];

    return [
      {
        field: "marketDate",
        headerName: t("MarketDate"),
        flex: 1.5,
        filter: false,
      },
      {
        field: "marketName",
        headerName: t("Market"),
        flex: 1.5,
        filter: false,
      },
      { field: "termName", headerName: t("Term"), flex: 2, filter: false },
      ...(tableState.product.qualityAttributes?.length
        ? tableState.product.qualityAttributes
            .filter(
              (qualityAttribute) => qualityAttribute.isPriceDriver !== false
            )
            .map((qualityAttribute) => ({
              field: qualityAttribute.qualityAttributeId,
              headerName: qualityAttribute.qualityAttributeName,
              flex: 1.5,
              filter: false,
            }))
        : []),
      // {
      //   field: "priceBasisName",
      //   headerName: t("PriceBasis"),
      //   flex: 1.5,
      //   filter: false,
      // },
      {
        field: "markPrice",
        headerName: t("MarkPrice"),
        flex: 1,
        filter: false,
      },
      { field: "bestBid", headerName: t("BestBid"), flex: 1, filter: false },
      {
        field: "bestOffer",
        headerName: t("BestOffer"),
        flex: 1,
        filter: false,
      },
      {
        field: "tradeCount",
        headerName: t("TradeCount"),
        flex: 1,
        filter: false,
      },
      {
        field: "tradeVolume",
        headerName: t("TradeVolume"),
        flex: 1,
        filter: false,
      },
      {
        field: "tradeWeightedAverage",
        headerName: t("TradeWeightedAverage"),
        flex: 1,
        filter: false,
      },
    ];
  }, [tableState.product, t]);

  // Initialize the table
  useEffect(() => {
    const initializeTable = async () => {
      // if (!indexBases || indexBases.length === 0) {
      //   setTableState((prev) => ({ ...prev, isLoading: false }));
      //   return;
      // }

      try {
        const mappedMarks = mapToViewModel(indexBases, market);

        setTableState({
          mappedMarks,
          market,
          product,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error initializing review marks table:", error);
        setTableState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initializeTable();
  }, [indexBases, market, product, mapToViewModel]);

  // Update marks when they change
  useEffect(() => {
    if (tableState.market && indexBases !== tableState.originalMarks) {
      const mappedMarks = mapToViewModel(indexBases, tableState.market);
      setTableState((prev) => ({
        ...prev,
        mappedMarks,
        originalMarks: indexBases,
      }));
    }
  }, [indexBases, tableState.market, tableState.originalMarks, mapToViewModel]);

  if (tableState.isLoading) return <Loading />;

  return (
    <div>
      <ThemeableAgGrid
        className="table-bold"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={tableState.mappedMarks}
        animateRows={true}
        //getRowId={(params) => params.data.indexBaseId.toString()}
        domLayout="autoHeight"
      />
    </div>
  );
};

export default IndexDataTable;
